import { Component, Vue, Watch, } from 'vue-property-decorator';
import { Model as DataModel } from '@/models/Model';
import ErrorHandler from '@/support/ErrorHandler';
import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import { styles } from '@/support/Style';
import { Avatar } from '@/support/Avatar';
import { Address } from '@/models/Address';
import LoaderCard from '@/components/loader-card/LoaderCard';
import MiPassword, { MiPasswordVisibility, MiPasswordIcons } from '@/components/mi-password/MiPassword';
import { InstanceLoader } from '@/support/InstanceLoader';
import { filtered_country_list } from '@/support/Countries';
import QuizAnswers from '@/components/quiz-anwsers/QuizAnswers';
import { LeadFile } from '@/models/LeadFile';
import { Lead } from '@/models/Lead';
import { Contact } from '@/models/Contact';
import { formatDate, copyStringToClipboard } from '@/support/String';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Order } from '@/models/Order';
import { DateTime } from 'luxon';
import { UserFile } from '@/models/UserFile';
import OrderDataTable from '@/views/Users/OrderDataTable';
import VueTelInput from 'vue-tel-input';
import { Supplier as SupplierModel } from '@/models/Supplier';

@Component<Supplier>({
  components: {
    LoaderCard,
    MiPassword,
    QuizAnswers,
    OrderDataTable,
    VueTelInput
  },
  filters: {
    formatTimeSeconds(time: string) {
      if (!time) {
        return;
      }
      return formatDate(time, 'dd-LL-yyyy HH:mm');
    },
    formatBirthday(date: string) {
      if(!date) {
        return '';
      }

      const birthday = DateTime.fromFormat(date, 'yyyy-LL-dd');
      if(!birthday.isValid) {
        return date;
      }

      const now = DateTime.local();
      return Math.floor((Math.abs(birthday.diff(now, 'years').toObject().years || 0)));
    },
  }
})
export default class Supplier extends Vue
{
  public $pageTitle = 'Leveranciers';
  protected user: DataModel = new DataModel();
  protected supplier: SupplierModel = new SupplierModel();
  protected supplierClone: SupplierModel | null = null;
  protected userFile: DataModel = new DataModel();
  protected address: Address | null = null;
  protected paazlOrders: paazlOrder[] = [];
  private type: string | null = null;
  protected currentOrders: Order[] | null = null;

  protected isEditingUser: boolean = false;
  protected isEditingAddress: boolean = false;
  protected isChangingPassword: boolean = false;
  protected isLoadingCountries: boolean = false;
  protected fullscreenInbox: boolean = false;
  protected isAddingExtraAddress: boolean = false;
  protected isOpeningQRDialog: boolean = false;

  protected isLoading: boolean = true;
  protected isUpdating: boolean = false;
  protected isSaved: boolean  = false;
  protected isLoadingButton: boolean = false;
  protected isSavedButton: boolean = false;
  protected copyLinkText: string = 'Kopieer stijlquiz link';

  protected isPasswordValid: boolean = false;
  protected activeTab: string = 'orders';

  protected stijlquizAnswers: any[] = [];
  protected stijlquizImages: any[] = [];

  protected phone: string = '';
  protected phoneValid: boolean = false;

  protected birthdayDatepicker: boolean = false;
  protected dateFormatted: string = '';

  protected userFinishedAddressEdit: boolean = false;
  public get canFinishOrder(): boolean {
    // if user must edit, he can't finish.
    return !this.userMustEditAddress || this.userFinishedAddressEdit
  }

  /**
   * currentOrderId
   *
   * coming from OrderOverview.ts
   * if user tries to finish order while address is not complete
   * the user is send back to personal edit page.
   * The string is the order number.
   *
   * @protected
   * @type {string}
   * @memberof User
   */
  protected currentOrderId: string = '';

  /**
   * userMustEditAddress
   * is dependend on currentOrderId
   * it's the related bool.
   *
   * @protected
   * @type {boolean}
   * @memberof User
   */
  protected userMustEditAddress: boolean = false;

  protected includes: {[key: string]: string[]} = {
    Leads: ['token'],
    Contacts: ['profile', 'answers', 'paazl', 'orders'],
    Stylists: ['token'],
    Admins: [],
  }

  protected images = {
    logo: '',
    invoice: '',
    login: '',
  }

  protected passwordVisibility: MiPasswordVisibility = {
    ruleList: false,
    showPassword: false,
  };

  protected icons: MiPasswordIcons = {
    password: 'lock_open',
    confirmation: 'lock_open'
  };

  public mounted() {
    this.$store.dispatch('changeBreadcrumbs', [{ name: this.type, path: `/${this.type}` }]);
    // @ts-ignore
    this.type = this.$route.meta.type;
    // @ts-ignore
    this.user = InstanceLoader.get(this.$route.meta.model);

    if(this.type === 'Leads') {
      this.userFile = InstanceLoader.get('LeadFile');
    } else if (this.type === 'Contacts') {
      this.userFile = InstanceLoader.get('UserFile');
    }
    this.initialize();

    // orderOverview has authority over user.... can demand address info
    this.currentOrderId = this.$route.query.hasOwnProperty('currentOrderId')
      ? String(this.$route.query.currentOrderId)
      : '';

    this.userMustEditAddress = this.currentOrderId.length !== 0; // orderNo is given by orderoverview



    // If the user must edit, he is editing.
    if (this.userMustEditAddress) this.isEditingUser = true;

    this.checkAddressMinimumRequirementsMet ();
  }

  protected checkAddressMinimumRequirementsMet (): void{
    const reqInputs: HTMLInputElement[] = Array.from(document.querySelectorAll('.minimum-required-address-fields input'));
    const reqInputsNotFilledIn = reqInputs.filter(input => !input.value);

    if (!reqInputsNotFilledIn.length) {
      this.userFinishedAddressEdit = true;
    }

  }

  protected finishOrder() {
    if (this.canFinishOrder) {
      this.$router.push(`/webshop/order/${this.$route.query.currentOrderId}/order-overview`);
    }
  }

  protected parsedStatus(status: string) {
    return status.replace(' ',  '_').toLowerCase();
  }

  protected get isEditingCurrentOrder() {
    return this.$route.query.editing;
  }

  public initialize() {
    if(!this.type) {
      return;
    }

    this.supplier
      .include(['invoice_address'])
    .find(this.$route.params.id)
    .then((supplier: SupplierModel) => {
      this.supplier = supplier;

      this.isLoading = false;
      this.initBreadcrumb();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected onPhoneInput(phoneInput: PhoneNumber) {
    this.phone = phoneInput.number;
    this.phoneValid = phoneInput.isValid;
  }

  protected initBreadcrumb() {
    this.$root.$emit('breadcrumbUpdated',
    {
      crumb: [
        { name: this.$t('navigation.users'), path: '/users' },
        { name: (this.user as any).name},
      ],
    });
  }

  protected get countries() {
    return filtered_country_list;
  }

  private clearSaved() {
    setTimeout(() => {
      this.isUpdating = false;
      this.isSaved = false;
      this.isEditingUser = false;
      this.isEditingAddress = false;
    }, 1200);
  }

  protected resetUser() {
    if (!this.supplierClone) {
      return;
    }

    this.supplier = cloneDeep(this.supplierClone);
    this.supplierClone = null;

    this.isChangingPassword = false;
    this.resetMiPassword();
  }

  protected canSubmit(password: string, confirmation: string) {
    if(!password || !confirmation) {
      return false;
    }
    return password === confirmation;
  }

  protected upgradeToContact() {
    new Lead()
    .upgradeToContact((this.user as Lead).id || '')
    .then(() => {
      this.$store.dispatch('openDialog', this.upgradeDialogOptions);
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  private get upgradeDialogOptions(): Options {
    return {
      title: 'Contact succesvol aangemaakt',
      text: 'Deze Lead is succesvol geconverteerd naar Contact',
      type: 'success',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.confirm').toString(),
        },
      },
    };
  }

  protected changeOrUpdatePassword() {
    if(this.isChangingPassword) {
      this.changePassword();
      this.isLoadingButton = true;
    } else {
      this.isChangingPassword = true;
    }
  }

  protected get canSavePassword() {
    if(!this.isChangingPassword) {
      return true;
    }

    if(this.isChangingPassword && this.isPasswordValid) {
      return true;
    }

    return false;
  }

  private changePassword() {
    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;
    if(!miPassword) {
      return;
    }

    const password = miPassword.getPassword().password;
    const password_confirmation = miPassword.getPassword().confirmation;

    if (!this.canSubmit(password, password_confirmation)) {
      return;
    }

    this.isLoadingButton = true;
    this.user
    .update({password, password_confirmation})
    .then(() => {
      this.isDelayingButtonFeedback();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected cancelEditPassword() {
    this.resetMiPassword();
    this.isChangingPassword = false;
  }

  protected get canChangePassword() {
    if(!this.user) {
      return false;
    }

    if (! this.$store.state.Auth) {
      return false;
    }

    return (this.user as any).id === this.$store.state.Auth.id;
  }

  protected update() {
    this.isUpdating = true;
    if (!this.supplier) {
      return;
    }

    this.supplier
    .update()
    .then(() => {
      this.isSaved = true;
      this.clearSaved();
      this.initialize();
    })
    .catch((error: AxiosError) => {
      this.clearSaved();
      this.initialize();
      ErrorHandler.network(error);
    });
  }

  protected isDelayingButtonFeedback() {
    setTimeout(() => {
      this.isLoadingButton = false;
      this.isSavedButton = true;
      this.isChangingPassword = false;

      setTimeout(() => {
        this.isSavedButton = false;
      }, 800);
    }, 1200);
  }

  private resetMiPassword() {
    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;
    if(miPassword) {
      miPassword.reset();
    }
  }

  protected handleAnswerUpdate(answers: any) {
    this.$set(this.user, 'answers', answers);
    this.update();
  }

  protected handleImageUpdate(images: any[]) {
    let callCount = images.length;

    images.forEach((file: string) => {
      (this.userFile as LeadFile | UserFile)
      .user((this.user as any).id)
      .create({file,})
      .then(() => {
        callCount -= 1;

        if(callCount <= 0) {
          this.fetchFiles();
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
    });
  }

  protected fetchFiles() {
    (this.userFile as LeadFile | UserFile)
    .user((this.user as any).id)
    .limit(100)
    .all()
    .then((images: any) => {
      this.stijlquizImages = images;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  protected fetchOrders() {
    new Order()
    .filter({user: this.$route.params.id, status: 'draft'})
    .all()
    .then((response: any) => {
      this.currentOrders = response;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected handleImageDelete(fileId: string) {
    (this.userFile as LeadFile | UserFile)
    .deleteUserFile((this.user as any).id, fileId)
    .then(()=> {
      this.fetchFiles();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected createOrReturn() {
    this.isEditingCurrentOrder ? this.returnToOrder() : this.createOrder();
  }

  protected returnToOrder() {
    this.$router.go(-1);
  }

  protected createOrder() {
    const orderPayload = {
      user: this.$route.params.id,
    }
    new Order()
    .create(orderPayload)
    .then((response: any) => {
      this.$router.push({
        name: 'webshop.order',
        params: { id: response.id }
      });
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected get passwordButtonText() {
    if(this.isChangingPassword) {
      return this.$t('profile.password.save').toString();
    }

    if (this.isSavedButton) {
      return this.$t('profile.password.saved').toString();
    }

    return this.$t('profile.password.change').toString();
  }

  protected get passwordButtonIcon() {
    if(this.isChangingPassword) {
      return 'save';
    }
    if (this.isSavedButton) {
      return 'check';
    }
    return 'lock';
  }

  protected get passwordButtonColor() {
    if(this.isChangingPassword) {
      return 'primary';
    }
    if (this.isSavedButton) {
      return 'success';
    }
    return 'warning';
  }

  protected get userAvatar() {
    if((this.supplier as any).avatar && !(this.supplier as any).avatar.includes('gravatar')) {
      return (this.supplier as any).avatar;
    }

    return new Avatar((this.supplier as any).name ? (this.supplier as any).name : 'NA', {color: styles['white'], background: styles['primary'], size: 512}).toDataUrl();
  }

  protected get showLeadContent() {
    return this.type && (this.type === 'Leads' || this.type === 'Contacts');
  }

  @Watch('isEditingUser')
  protected editingChanged(isEditing: boolean) {
    if (isEditing) {
      this.supplierClone = cloneDeep(this.supplier);
    }
  }

  @Watch('$route')
  public routeChanged() {
    this.initialize();
  }
}

interface paazlOrder {
  created_at: string;
  reference_number: string;
}


interface PhoneNumber {
  number: string;
  isValid: boolean;
  country: object;
}
